import { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import Modal from '@/components/base/Modal';
import { useToast } from '@/components/base/Toast';

// ---------- Tipos compartidos ----------
interface ObraContable {
  id: string;
  nombre: string;
  clienteNombre: string;
  estado: string;
  totalAutorizado: number;
  totalPagado: number;
  deuda: number;
  totalFacturado: number;
}

interface ProveedorContable {
  id: string;
  nombre: string;
  tipo: string;
  totalCompras: number;
  totalPagado: number;
  deuda: number;
}

interface Factura {
  id: string;
  obraId: string;
  obraNombre: string;
  clienteId: string;
  clienteNombre: string;
  numeroFactura: string;
  monto: number;
  fecha: string;
  comprobanteUrl?: string | null;
}

interface PagoEmpleado {
  trabajadorId: string;
  trabajadorNombre: string;
  obraId: string;
  obraNombre: string;
  montoTotal: number;
  pagoId: string;
  pagoFecha: string;
  pagoConcepto: string;
  pagoMonto: number;
}

// ---------- Helpers ----------
const API_URL = 'https://api.airmex.mx/api/v1';

function getToken() {
  return localStorage.getItem('airmex_token') || sessionStorage.getItem('airmex_token');
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...((options.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) throw new Error('Error en la petición');
  if (res.status === 204) return null;
  return res.json();
}

function formatMXN(monto: number) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(monto);
}

// ---------- Pestañas ----------
type Tab = 'obras' | 'proveedores' | 'facturas' | 'empleados';

export default function ContabilidadPage() {
  const [activeTab, setActiveTab] = useState<Tab>('obras');

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto space-y-6">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">Contabilidad</h1>
          <p className="text-sm text-foreground-500 mt-1">Resumen financiero de obras, proveedores, facturas y empleados</p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-background-200">
          {([
            { key: 'obras', label: 'Obras', icon: 'ri-building-line' },
            { key: 'proveedores', label: 'Proveedores', icon: 'ri-store-2-line' },
            { key: 'facturas', label: 'Facturas', icon: 'ri-bill-line' },
            { key: 'empleados', label: 'Empleados', icon: 'ri-team-line' },
            
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium flex items-center gap-1.5 border-b-2 transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab.key
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-foreground-500 hover:text-foreground-700'
                }`}
            >
              <i className={tab.icon} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Contenido dinámico */}
        {activeTab === 'obras' && <ObrasTab />}
        {activeTab === 'proveedores' && <ProveedoresTab />}
        {activeTab === 'facturas' && <FacturasTab />}
        {activeTab === 'empleados' && <EmpleadosTab />}
      </div>
    </DashboardLayout>
  );
}

// ---------- Pestaña Obras ----------
function ObrasTab() {
  const { showToast } = useToast();
  const [obras, setObras] = useState<ObraContable[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filtroEstado) params.append('estado', filtroEstado);
      const data = await fetchWithAuth(`/contabilidad/obras?${params.toString()}`);
      setObras(data);
    } catch (err: any) {
      showToast('error', 'Error al cargar obras', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); load(); };

  return (
    <div>
      <form onSubmit={handleSearch} className="flex flex-wrap gap-3 mb-4">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar obra o cliente" className="px-3 py-2 rounded-lg border text-sm flex-1 min-w-[200px]" />
        <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} className="px-3 py-2 rounded-lg border text-sm">
          <option value="">Todos los estados</option>
          <option value="activa">Activa</option>
          <option value="finalizada">Finalizada</option>
          <option value="cancelada">Cancelada</option>
        </select>
        <button type="submit" className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600">
          <i className="ri-search-line mr-1" /> Buscar
        </button>
      </form>

      {loading ? (
        <div className="flex justify-center py-10"><i className="ri-loader-4-line animate-spin text-2xl" /></div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background-100/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500">Obra</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 hidden md:table-cell">Cliente</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500">Facturado</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500">Dinero Tomado</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500">Deuda</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {obras.map(o => (
                <tr key={o.id} className="hover:bg-background-50">
                  <td className="px-4 py-3">
                    <span className="text-xs font-medium">{o.nombre}</span>
                    <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full ${o.estado === 'activa' ? 'bg-emerald-100 text-emerald-700' :
                        o.estado === 'finalizada' ? 'bg-slate-100 text-slate-600' :
                          'bg-red-100 text-red-600'
                      }`}>{o.estado}</span>
                  </td>
                  <td className="px-4 py-3 text-xs hidden md:table-cell">{o.clienteNombre}</td>
                  <td className="px-4 py-3 text-right text-xs font-semibold">{formatMXN(o.totalFacturado)}</td>
                  <td className="px-4 py-3 text-right text-xs font-semibold text-green-600">{formatMXN(o.totalPagado)}</td>
                  <td className={`px-4 py-3 text-right text-xs font-semibold ${o.deuda > 0 ? 'text-red-600' : o.deuda < 0 ? 'text-amber-600' : 'text-green-600'
                    }`}>{formatMXN(Math.abs(o.deuda))}</td>
                </tr>
              ))}
              {obras.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-xs text-foreground-400">No se encontraron obras.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- Pestaña Proveedores ----------
function ProveedoresTab() {
  const { showToast } = useToast();
  const [pagos, setPagos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [proveedorId, setProveedorId] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [proveedoresList, setProveedoresList] = useState<{ id: string; nombre: string }[]>([]);

  // Cargar lista de proveedores al montar
  useEffect(() => {
    const loadLists = async () => {
      try {
        const provs = await fetchWithAuth('/proveedores/');
        setProveedoresList(provs.map((p: any) => ({ id: p.id, nombre: p.nombre })));
      } catch {}
    };
    loadLists();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (proveedorId) params.append('proveedor_id', proveedorId);
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      const data = await fetchWithAuth(`/contabilidad/pagos_proveedores?${params.toString()}`);
      setPagos(data);
    } catch (err: any) {
      showToast('error', 'Error al cargar pagos a proveedores', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); load(); };

  return (
    <div>
      <form onSubmit={handleSearch} className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar proveedor o concepto"
          className="px-3 py-2 rounded-lg border text-sm flex-1 min-w-[200px]"
        />
        <select
          value={proveedorId}
          onChange={(e) => setProveedorId(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm min-w-[180px]"
        >
          <option value="">Todos los proveedores</option>
          {proveedoresList.map(p => (
            <option key={p.id} value={p.id}>{p.nombre}</option>
          ))}
        </select>
        <input
          type="date"
          value={fechaDesde}
          onChange={(e) => setFechaDesde(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm"
        />
        <input
          type="date"
          value={fechaHasta}
          onChange={(e) => setFechaHasta(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm"
        />
        <button type="submit" className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600">
          <i className="ri-search-line mr-1" /> Buscar
        </button>
      </form>

      {loading ? (
        <div className="flex justify-center py-10"><i className="ri-loader-4-line animate-spin text-2xl" /></div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background-100/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500">Proveedor</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 hidden md:table-cell">Concepto</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 hidden sm:table-cell">Fecha</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500">Monto</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500">Cmp.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {pagos.map((pago, idx) => (
                <tr key={pago.pagoId || idx} className="hover:bg-background-50">
                  <td className="px-4 py-3 text-xs font-medium">{pago.proveedorNombre}</td>
                  <td className="px-4 py-3 text-xs hidden md:table-cell">{pago.pagoConcepto}</td>
                  <td className="px-4 py-3 text-xs hidden sm:table-cell">
                    {new Date(pago.pagoFecha + 'T00:00:00').toLocaleDateString('es-MX')}
                  </td>
                  <td className="px-4 py-3 text-right text-xs font-semibold text-green-600">
                    {formatMXN(pago.pagoMonto)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {pago.pagoComprobanteUrl ? (
                      <a href={pago.pagoComprobanteUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100">
                        <i className="ri-file-line text-xs" />
                      </a>
                    ) : (
                      <span className="text-foreground-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {pagos.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-xs text-foreground-400">
                    No se encontraron pagos a proveedores.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- Pestaña Facturas ----------
function FacturasTab() {
  const { showToast } = useToast();
  const [facturas, setFacturas] = useState<Factura[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [montoMin, setMontoMin] = useState('');
  const [montoMax, setMontoMax] = useState('');
  const [selectedFactura, setSelectedFactura] = useState<Factura | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      if (montoMin) params.append('monto_min', montoMin);
      if (montoMax) params.append('monto_max', montoMax);
      const data = await fetchWithAuth(`/facturas?${params.toString()}`);
      setFacturas(data);
    } catch (err: any) {
      showToast('error', 'Error al cargar facturas', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); load(); };

  return (
    <div>
      <form onSubmit={handleSearch} className="flex flex-wrap gap-3 mb-4">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar cliente, obra o n° factura" className="px-3 py-2 rounded-lg border text-sm flex-1 min-w-[200px]" />
        <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="px-3 py-2 rounded-lg border text-sm" />
        <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="px-3 py-2 rounded-lg border text-sm" />
        <input type="number" value={montoMin} onChange={(e) => setMontoMin(e.target.value)} placeholder="Monto mín" className="w-28 px-3 py-2 rounded-lg border text-sm" />
        <input type="number" value={montoMax} onChange={(e) => setMontoMax(e.target.value)} placeholder="Monto máx" className="w-28 px-3 py-2 rounded-lg border text-sm" />
        <button type="submit" className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600">
          <i className="ri-search-line mr-1" /> Buscar
        </button>
      </form>

      {loading ? (
        <div className="flex justify-center py-10"><i className="ri-loader-4-line animate-spin text-2xl" /></div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background-100/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500">N° Factura</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500">Cliente</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 hidden md:table-cell">Obra</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500">Monto</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 hidden sm:table-cell">Fecha</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {facturas.map(f => (
                <tr key={f.id} className="hover:bg-background-50">
                  <td className="px-4 py-3 font-mono text-xs">{f.numeroFactura}</td>
                  <td className="px-4 py-3 text-xs">{f.clienteNombre}</td>
                  <td className="px-4 py-3 text-xs hidden md:table-cell">{f.obraNombre}</td>
                  <td className="px-4 py-3 text-right font-semibold text-xs">{formatMXN(f.monto)}</td>
                  <td className="px-4 py-3 text-xs hidden sm:table-cell">{new Date(f.fecha + 'T00:00:00').toLocaleDateString('es-MX')}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => setSelectedFactura(f)} className="w-7 h-7 rounded-lg hover:bg-blue-100 text-blue-600" title="Ver detalle"><i className="ri-eye-line" /></button>
                      {f.comprobanteUrl && (
                        <a href={f.comprobanteUrl} target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-lg hover:bg-green-100 text-green-600 flex items-center justify-center" title="Descargar"><i className="ri-download-line" /></a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {facturas.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-xs text-foreground-400">No se encontraron facturas.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal detalle factura */}
      {selectedFactura && (
        <Modal
          open={!!selectedFactura}
          onClose={() => setSelectedFactura(null)}
          title={`Detalle de ${selectedFactura.numeroFactura}`}
          size="sm"
          footer={<button onClick={() => setSelectedFactura(null)} className="px-4 py-2 rounded-lg border text-sm">Cerrar</button>}
        >
          <div className="space-y-3 text-sm">
            <div><span className="text-foreground-500">Cliente:</span> <strong>{selectedFactura.clienteNombre}</strong></div>
            <div><span className="text-foreground-500">Obra:</span> <strong>{selectedFactura.obraNombre}</strong></div>
            <div><span className="text-foreground-500">Monto:</span> <strong>{formatMXN(selectedFactura.monto)}</strong></div>
            <div><span className="text-foreground-500">Fecha:</span> <strong>{selectedFactura.fecha}</strong></div>
            {selectedFactura.comprobanteUrl && (
              <div className="pt-2">
                <a href={selectedFactura.comprobanteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-xs"><i className="ri-download-line" /> Descargar factura</a>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------- NUEVA Pestaña Empleados ----------
function EmpleadosTab() {
  const { showToast } = useToast();
  const [empleados, setEmpleados] = useState<PagoEmpleado[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [trabajadorId, setTrabajadorId] = useState('');
  const [obraId, setObraId] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  // Listas para los selectores
  const [trabajadoresList, setTrabajadoresList] = useState<{ id: string; nombre: string }[]>([]);
  const [obrasList, setObrasList] = useState<{ id: string; nombre: string }[]>([]);

  // Cargar listas al montar
  useEffect(() => {
    const loadLists = async () => {
      try {
        const [trab, obr] = await Promise.all([
          fetchWithAuth('/trabajadores/'),
          fetchWithAuth('/todas')   // endpoint de obras
        ]);
        setTrabajadoresList(trab.map((t: any) => ({ id: t.id, nombre: t.nombre })));
        setObrasList(obr.map((o: any) => ({ id: o.id, nombre: o.nombre })));
      } catch (err: any) {
        // No crítico, se puede usar la búsqueda manual
      }
    };
    loadLists();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (trabajadorId) params.append('trabajador_id', trabajadorId);
      if (obraId) params.append('obra_id', obraId);
      if (fechaDesde) params.append('fecha_desde', fechaDesde);
      if (fechaHasta) params.append('fecha_hasta', fechaHasta);
      const data = await fetchWithAuth(`/contabilidad/empleados?${params.toString()}`);
      setEmpleados(data);
    } catch (err: any) {
      showToast('error', 'Error al cargar datos', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); load(); };

  return (
    <div>
      <form onSubmit={handleSearch} className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar trabajador, obra o concepto"
          className="px-3 py-2 rounded-lg border text-sm flex-1 min-w-[200px]"
        />
        <select
          value={trabajadorId}
          onChange={(e) => setTrabajadorId(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm min-w-[180px]"
        >
          <option value="">Todos los trabajadores</option>
          {trabajadoresList.map(t => (
            <option key={t.id} value={t.id}>{t.nombre}</option>
          ))}
        </select>
        <select
          value={obraId}
          onChange={(e) => setObraId(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm min-w-[180px]"
        >
          <option value="">Todas las obras</option>
          {obrasList.map(o => (
            <option key={o.id} value={o.id}>{o.nombre}</option>
          ))}
        </select>
        <input
          type="date"
          value={fechaDesde}
          onChange={(e) => setFechaDesde(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm"
        />
        <input
          type="date"
          value={fechaHasta}
          onChange={(e) => setFechaHasta(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm"
        />
        <button type="submit" className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600">
          <i className="ri-search-line mr-1" /> Buscar
        </button>
      </form>

      {/* Tabla de resultados (igual que antes) */}
      {loading ? (
        <div className="flex justify-center py-10"><i className="ri-loader-4-line animate-spin text-2xl" /></div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background-100/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500">Trabajador</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500">Obra</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 hidden md:table-cell">Concepto</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 hidden sm:table-cell">Fecha</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500">Monto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {empleados.map((pago, idx) => (
                <tr key={pago.pagoId || idx} className="hover:bg-background-50">
                  <td className="px-4 py-3 text-xs font-medium">{pago.trabajadorNombre}</td>
                  <td className="px-4 py-3 text-xs">{pago.obraNombre}</td>
                  <td className="px-4 py-3 text-xs hidden md:table-cell">{pago.pagoConcepto}</td>
                  <td className="px-4 py-3 text-xs hidden sm:table-cell">
                    {new Date(pago.pagoFecha + 'T00:00:00').toLocaleDateString('es-MX')}
                  </td>
                  <td className="px-4 py-3 text-right text-xs font-semibold text-green-600">
                    {formatMXN(pago.pagoMonto)}
                  </td>
                </tr>
              ))}
              {empleados.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-xs text-foreground-400">
                    No se encontraron pagos a empleados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}